import { createSelector } from 'reselect';
import { getFormValues } from 'redux-form';

import { Forms } from '../../form/form.selector';
import { DataToolsListPageFormValues } from '../../../routes/Manage/DataTools/DataToolsPage/DataToolsListPage/DataToolsListPage';
import { RootState } from '../../reducers';
import {
  BatchedLoadDeliveryData,
  Concentrate,
  DateRangeDefined,
  groupTypeFilters,
  LoadDeliveryData,
  Milking,
} from './dataTools.types';
import { DataToolsFilterForm } from '../../../routes/Manage/DataTools/DataToolsPage/DataToolsSpecificPage/DataToolsSpecificPageToolbar/DataToolsSpecificPageToolbar';
import { getRecipes } from '../recipes/recipes.selector';
import { getPremixes } from '../premixes/premixes.selector';
import { getIngredients } from '../ingredients/ingredients.selector';
import { Premix } from '../premixes/premixes.types';
import { Recipe } from '../recipes/recipes.types';
import { getAllGroups } from '../group/group.selector';
import { getGroupTypeList } from '../groupType/groupType.selector';
import {
  getIngredientDryMatterSettingChangesItem,
  getIngredientInventoryChangesItem,
  getIngredientPricesSettingChangesItem,
  getIngredientSettingChangesItem,
  getPenSettingChangesItem,
  getRationSettingChangesItem,
} from '../reports/reports.selector';
import {
  IngredientSettingChangesItem,
  InventoryHistoryItem,
  PenSettingChangesItem,
  RationSettingChangesItem,
  ReportGridData,
} from '../reports/reports.types';

export const getSelectedTimeRange = createSelector(
  [getFormValues(Forms.dataToolsPageForm) as any],
  (dataToolsFormValues: DataToolsListPageFormValues): DateRangeDefined => {
    const selectedTimeRange = dataToolsFormValues ? dataToolsFormValues.selectedTimeRange : undefined;
    if (!selectedTimeRange) {
      return { startDate: new Date(), endDate: new Date() };
    }
    return {
      startDate: selectedTimeRange.startDate || new Date(),
      endDate: selectedTimeRange.endDate || new Date(),
    };
  },
);

export const getLoadsDeliveriesData = (state: RootState) => state.entities.dataTools.loadsdeliveries;

export const getLoadDeliveryDataById = createSelector(
  [getLoadsDeliveriesData, (state: RootState, batchId: string) => batchId],
  (batches: LoadDeliveryData[], batchId: string) => batches.find((batch) => batch.id === batchId),
);

export const getLoadsDeliveriesBatchedByDays = createSelector(
  [getLoadsDeliveriesData],
  (data: LoadDeliveryData[]): BatchedLoadDeliveryData[] => {
    if (!data) return [];

    const dict = {};
    data.forEach((d) => {
      const date = new Date(d.endDateTime).setHours(0, 0, 0, 0);
      if (dict[date]) {
        dict[date] = [...dict[date], d];
      } else {
        dict[date] = [d];
      }
    });

    const result: BatchedLoadDeliveryData[] = Object.keys(dict).map((millis) => ({
      batchDate: new Date(parseInt(millis, 10)),
      data: dict[millis],
    }));

    return result;
  },
);

export const getLoadsDeliveriesFilteredBySearch = createSelector(
  [getLoadsDeliveriesBatchedByDays, getFormValues(Forms.dataToolsSearchForm), getRecipes, getPremixes],
  (batchedData: BatchedLoadDeliveryData[], formValue: DataToolsFilterForm, recipes: Recipe[], premixes: Premix[]) => {
    if (!formValue) return batchedData;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return batchedData;

    const filteredRations = [...recipes, ...premixes]
      .filter((ration) => ration.description.toLowerCase().includes(search))
      .map((ration) => ration.id);

    return batchedData
      .map((b) => {
        const filteredData = b.data.filter((d) => filteredRations.includes(d.rationId));
        return {
          ...b,
          data: filteredData,
        };
      })
      .filter((b) => b.data.length !== 0);
  },
);

export const getIngredientPrices = (state: RootState) => state.entities.dataTools.ingredientprices;

export const getIngredientPricesById = createSelector([getIngredientPrices], (ingredientsPrices) => (id: string) => {
  return ingredientsPrices.find((ip) => ip.ingredientId === id);
});

export const getIngredientsFilteredBySearch = createSelector(
  [getIngredients, getFormValues(Forms.dataToolsSearchForm)],
  (ingredients, formValue: DataToolsFilterForm) => {
    if (!formValue) return ingredients;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return ingredients;

    return ingredients.filter((ingredient) => ingredient.description.toLowerCase().includes(search));
  },
);

export const getMilkings = (state: RootState) => state.entities.dataTools.milkproduction;

export const getMilkingsById = createSelector(
  [getMilkings],
  (concentrates: Milking[]) => (id: string) => concentrates.filter((elem) => elem.groupId === id),
);

export const getGroupsFilteredByType = createSelector(
  [getAllGroups, getGroupTypeList, getFormValues(Forms.dataToolsSpecificPageToolbarForm)],
  (groups, groupTypeList, formValues: any) => {
    const noPremixes = groups.filter((elem) => !elem.premix);
    switch (formValues.penFilterType) {
      case groupTypeFilters.MILKING:
        return noPremixes.filter((group) => {
          const milkingGroupTypes = groupTypeList.filter((groupType) => groupType.groupClassCode === 11);
          return !group.deleted && milkingGroupTypes.some((groupType) => groupType.id === group.groupTypeId);
        });
      case groupTypeFilters.ALL_PENS:
        return noPremixes.filter((elem) => !elem.deleted && !elem.notInUse);
      case groupTypeFilters.INCLUDE_NOT_IN_USE:
        return noPremixes.filter((elem) => !elem.deleted);
      case groupTypeFilters.INCLUDE_DELETED:
        return noPremixes;
      default:
        return [];
    }
  },
);

export const getGroupsFilteredBySearch = createSelector(
  [getGroupsFilteredByType, getFormValues(Forms.dataToolsSearchForm)],
  (groups, formValue: DataToolsFilterForm) => {
    if (!formValue) return groups;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return groups;

    return groups.filter((group) => group.description.toLowerCase().includes(search));
  },
);

export const getPenChangesFilterdBySearch = createSelector(
  [getPenSettingChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<PenSettingChangesItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter((elem) => elem.groupName.toLowerCase().includes(search));
  },
);

export const getRationChangesFilterdBySearch = createSelector(
  [getRationSettingChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<RationSettingChangesItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter(
      (elem) =>
        elem.rationName.toLowerCase().includes(search) ||
        (elem.ingredientDescripion && elem.ingredientDescripion.toLowerCase().includes(search)),
    );
  },
);

export const getIngredientChangesFilterdBySearch = createSelector(
  [getIngredientSettingChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<IngredientSettingChangesItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter((elem) => elem.ingredientName.toLowerCase().includes(search));
  },
);

export const getIngredientPricesSettingChangesFilterBySearch = createSelector(
  [getIngredientPricesSettingChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<IngredientSettingChangesItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter((elem) => elem.ingredientName.toLowerCase().includes(search));
  },
);

export const getIngredientDryMatterSettingChangesFilterBySearch = createSelector(
  [getIngredientDryMatterSettingChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<IngredientSettingChangesItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter((elem) => elem.ingredientName.toLowerCase().includes(search));
  },
);

export const getIngredientInventoryChangesFilterBySearch = createSelector(
  [getIngredientInventoryChangesItem, getFormValues(Forms.dataToolsSearchForm)],
  (reportData: ReportGridData<InventoryHistoryItem>, formValue: DataToolsFilterForm) => {
    if (!reportData) return [];
    if (!formValue) return reportData.items;
    const search = formValue.search.trim().toLowerCase();
    if (!search) return reportData.items;

    return reportData.items.filter((elem) => elem.ingredientName.toLowerCase().includes(search));
  },
);

export const getConcentrates = (state: RootState) => state.entities.dataTools.externalconcentrates;

export const getConcentratesById = createSelector(
  [getConcentrates],
  (concentrates: Concentrate[]) => (id: string) => concentrates.filter((elem) => elem.groupId === id),
);

export const getMilkPrices = (state: RootState) => state.entities.dataTools.milkprices;
